import Component from '@glimmer/component';
import { service } from '@ember/service';
import Store from '@ember-data/store';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import fuzzysort from 'fuzzysort';
import JournalGroupUserModel from 'donor-elf-web/models/all-funds/journal-group-user';
import AllFundsUserModel from 'donor-elf-web/models/all-funds/user';

interface ComponentArgs {
  existingJournalGroupUsers: JournalGroupUserModel[];
  onSave: (addedUsers: JournalGroupAddUserItem[]) => void;
  onCancel: () => void;
}

export class JournalGroupAddUserItem {
  constructor(public user: AllFundsUserModel) {
    this.label = `${user.lastName}, ${user.firstName}`;
    this.labelPrepared = fuzzysort.prepare(this.label);
  }

  @tracked selected = false;
  @tracked isOwner = false;
  label: string;
  labelPrepared: Fuzzysort.Prepared;
  @tracked fuzzyResult?: Fuzzysort.KeyResult<JournalGroupAddUserItem>;
}

export default class JournalGroupAddUsersComponent extends Component<ComponentArgs> {
  @service declare store: Store;

  @tracked searchVal = '';
  @tracked userItems: JournalGroupAddUserItem[] = [];
  @tracked loading = true;

  @action
  templateDidInsert() {
    this.load();
  }

  async load() {
    this.loading = true;
    let existingUserIds: { [id: string]: boolean } = {};
    for (let journalGroupUser of this.args.existingJournalGroupUsers) {
      existingUserIds[journalGroupUser.userId] = true;
    }

    let allUsers = (await this.store.query('all-funds/user', {})).slice();
    this.userItems = sortByProperties(
      allUsers
        .filter((user: AllFundsUserModel) => {
          return existingUserIds[user.id] != true;
        })
        .map((user: AllFundsUserModel) => {
          return new JournalGroupAddUserItem(user);
        }),
      ['user.lastName:asc', 'user.firstName:asc'],
    );
    this.loading = false;
  }

  get filteredUserItems() {
    if ((this.searchVal || '').length < 3) {
      for (let userItem of this.userItems) {
        userItem.fuzzyResult = undefined;
      }
      return this.userItems;
    } else {
      return fuzzysort
        .go(this.searchVal, this.userItems, {
          threshold: -10000,
          key: ['labelPrepared'],
        })
        .map((fuzzyResult: Fuzzysort.KeyResult<JournalGroupAddUserItem>) => {
          fuzzyResult.obj.fuzzyResult = fuzzyResult;
          return fuzzyResult.obj;
        });
    }
  }

  @action
  handleSearchInput(e: any) {
    this.searchVal = e.target.value;
  }

  @action
  toggleIsOwner(userItem: JournalGroupAddUserItem) {
    userItem.isOwner = !userItem.isOwner;
    if (userItem.isOwner && !userItem.selected) {
      userItem.selected = true;
    }
  }

  @action
  toggleSelected(userItem: JournalGroupAddUserItem) {
    userItem.selected = !userItem.selected;
    if (!userItem.selected && userItem.isOwner) {
      userItem.isOwner = false;
    }
  }

  @action
  save() {
    this.args.onSave(this.userItems.filter((userItem) => userItem.selected));
  }
}
